import { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { authCredentialsAtom, isAuthenticatedAtom, errorAtom } from '@/lib/store';
import { AuthService } from '@/services/auth';
import { AuthCredentials } from '@/types';
import { ApiService } from '@/services/api';
import { sprintsAtom, issuesAtom, projectsAtom, userAllocationsAtom, isLoadingAtom } from '@/lib/store';
import { calculateUserAllocations } from '@/lib/planning';

export function useAuth() {
  const [authCredentials, setAuthCredentials] = useAtom(authCredentialsAtom);
  const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
  const [, setError] = useAtom(errorAtom);
  const [rememberMe, setRememberMe] = useState(false);
  const [, setSprints] = useAtom(sprintsAtom);
  const [, setIssues] = useAtom(issuesAtom);
  const [, setProjects] = useAtom(projectsAtom);
  const [, setUserAllocations] = useAtom(userAllocationsAtom);
  const [, setIsLoading] = useAtom(isLoadingAtom);
  
  // Load saved credentials and auto-login on hook initialization
  useEffect(() => {
    const autoLogin = async () => {
      const savedCredentials = AuthService.loadCredentials();
      if (savedCredentials) {
        setAuthCredentials(savedCredentials);
        setRememberMe(true);
        
        // Check if we're in a logged-in state
        const authStatus = localStorage.getItem('auth_status');
        if (authStatus === 'true') {
          setIsAuthenticated(true);
          
          // Auto-fetch data
          try {
            setIsLoading(true);
            const storyPointField = savedCredentials.storyPointField || 'customfield_10022';
            const data = await ApiService.fetchSprintData(savedCredentials, storyPointField);
            
            const { sprints, issues, projects } = data;
            const userAllocations = calculateUserAllocations(issues, sprints);
            
            setSprints(sprints);
            setIssues(issues);
            setProjects(projects || []);
            setUserAllocations(userAllocations);
          } catch (err: unknown) {
            const error = err as Error;
            console.error('Data fetch error:', error);
            setError(error.message || 'Failed to fetch data');
          } finally {
            setIsLoading(false);
          }
        }
      }
    };
    
    autoLogin();
  }, [setAuthCredentials, setIsAuthenticated, setIsLoading, setSprints, setIssues, setProjects, setUserAllocations, setError]);

  const login = async (credentials: AuthCredentials, remember: boolean) => {
    // Save credentials to localStorage if remember me is checked
    AuthService.saveCredentials(credentials, remember);
    
    // Update state
    setAuthCredentials(credentials);
    setIsAuthenticated(true);
  };

  const logout = () => {
    // Mark as logged out but preserve credentials
    AuthService.clearCredentials();
    setIsAuthenticated(false);
    // Reset all data states
    setSprints([]);
    setIssues([]);
    setProjects([]);
    setUserAllocations([]);
  };

  return {
    authCredentials,
    isAuthenticated,
    rememberMe,
    setRememberMe,
    login,
    logout
  };
}